import React, { useState, useRef, useEffect } from "react";
import { styled, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

// Colores del tema (puedes ajustarlos a tu gusto)
const COLORS = {
  BG: "#FFFFFF",
  TEXT: "#1E293B",
  TEXT_SECONDARY: "#64748B",
  BORDER: "#E2E8F0",
  PRIMARY: "#3B82F6", // Azul moderno
  HOVER: "#F8FAFC",
  SHADOW: "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
};

const SelectContainer = styled("div")({
  position: "relative",
  width: "100%",
  minWidth: "180px", // Ancho mínimo para que no se vea aplastado
});

const CustomSelectDisplay = styled("div")(({ isOpen }) => ({
  height: 42,
  padding: "0 16px",
  background: COLORS.BG,
  borderRadius: "12px", // Bordes redondeados modernos (no tipo píldora extrema)
  border: `1px solid ${isOpen ? COLORS.PRIMARY : COLORS.BORDER}`, // Borde azul si está abierto
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: isOpen ? `0 0 0 3px rgba(59, 130, 246, 0.1)` : "none", // Efecto focus sutil
  "&:hover": {
    borderColor: isOpen ? COLORS.PRIMARY : "#CBD5E1",
    backgroundColor: COLORS.HOVER,
  },
}));

const SelectedLabel = styled(Typography)({
  color: COLORS.TEXT,
  fontSize: "0.875rem", // 14px
  fontWeight: 500,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginRight: "8px",
});

const OptionsContainer = styled("div")({
  position: "absolute",
  top: "calc(100% + 6px)", // Un poco separado del input
  left: 0,
  right: 0,
  background: COLORS.BG,
  boxShadow: COLORS.SHADOW,
  borderRadius: "12px",
  border: `1px solid ${COLORS.BORDER}`,
  zIndex: 1000,
  maxHeight: "250px", // Altura máxima para scroll
  overflowY: "auto",
  padding: "6px",
  // Estilo del scrollbar
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#CBD5E1",
    borderRadius: "4px",
  },
});

const Option = styled("div")(({ isSelected }) => ({
  color: isSelected ? COLORS.PRIMARY : COLORS.TEXT,
  fontSize: "0.875rem",
  fontWeight: isSelected ? 600 : 400,
  padding: "10px 12px",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background 0.2s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: isSelected ? "rgba(59, 130, 246, 0.08)" : "transparent",
  "&:hover": {
    backgroundColor: isSelected ? "rgba(59, 130, 246, 0.12)" : COLORS.HOVER,
  },
}));

// Icono rotatorio
const ArrowIcon = styled(KeyboardArrowDownRoundedIcon)(({ isOpen }) => ({
  color: COLORS.TEXT_SECONDARY,
  transition: "transform 0.3s ease",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
}));

const CustomSelect = ({ value, onChange, valuesFilter = [], defaultLabel = "Todas las categorías" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Manejar selección
  const handleOptionClick = (val) => {
    onChange(val); // Enviamos el evento al padre
    setIsOpen(false);
  };

  // Filtramos ítems con id vacío/nulo antes de buscar la opción seleccionada,
  // ya que esos representan el placeholder y causarían duplicación.
  const filteredOptions = valuesFilter.filter(
    (item) => item.id !== "" && item.id !== null && item.id !== undefined
  );

  // Encontrar el título de la opción seleccionada
  const selectedItem = filteredOptions.find((item) => item.id === value);
  const selectedLabel = selectedItem ? selectedItem.title : defaultLabel;

  // Click outside para cerrar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <SelectContainer ref={containerRef}>
      
      {/* Input Display */}
      <CustomSelectDisplay 
        onClick={() => setIsOpen(!isOpen)} 
        isOpen={isOpen}
      >
        <SelectedLabel>
          {selectedLabel}
        </SelectedLabel>
        <ArrowIcon isOpen={isOpen} />
      </CustomSelectDisplay>

      {/* Dropdown Menu */}
      {isOpen && (
        <OptionsContainer>
          {/* Opción por defecto configurable (siempre visible, nunca duplicada) */}
          <Option 
            onClick={() => handleOptionClick("")}
            isSelected={value === "" || value === null || value === undefined}
          >
            {defaultLabel}
          </Option>

          {/* Lista de opciones dinámicas — ya sin ítems de id vacío/nulo */}
          {filteredOptions.map((role) => (
            <Option 
              key={role.id} 
              onClick={() => handleOptionClick(role.id)}
              isSelected={value === role.id}
            >
              {role.title}
            </Option>
          ))}
        </OptionsContainer>
      )}

    </SelectContainer>
  );
};

export default CustomSelect;